import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DialogContext, IDialogContextProps } from '../../services';
import { IObservedRootStore } from '../../stores';
import { InactivityDialog } from './inactivity-dialog';

const inactivityCountdown: number = 15 * 60; // seconds

export const InactivityDetector: React.FC<IObservedRootStore> = inject('rootStore')(
    observer(
        (props: IObservedRootStore): React.ReactElement => {
            const { rootStore } = props;
            if (!rootStore) {
                return <></>;
            }
            const dialogContext = useContext(DialogContext) as IDialogContextProps;
            const [countdown, setCountdown] = useState<number>(inactivityCountdown);
            const [isTiming, setTiming] = useState(true);

            const resetInactivityCountdown = () => {
                setCountdown(inactivityCountdown);
            };

            const hideDialog = useCallback((): void => {
                dialogContext.hideDialog();
                setTiming(true);
                resetInactivityCountdown();
            }, [dialogContext]);

            const showDialog = useCallback((): void => {
                setTiming(false);

                dialogContext.showDialog({
                    props: {
                        onClose: hideDialog,
                        disableBackdropClick: true,
                        disableEscapeKeyDown: true,
                        renderTitle: 'Are you still there?',
                    },
                    Component: <InactivityDialog onClose={hideDialog} />,
                });
            }, [dialogContext, hideDialog]);

            const handleKeyDown = useCallback(
                (e: KeyboardEvent): void => {
                    if (e.altKey && e.code === 'Minus') {
                        hideDialog();
                    } else if (e.altKey && e.code === 'Equal') {
                        showDialog();
                    }
                },
                [hideDialog, showDialog],
            );

            useEffect(() => {
                const t = setInterval(() => {
                    if (isTiming) {
                        setCountdown(countdown - 1);
                    }
                }, 1000);
                return () => {
                    clearInterval(t);
                };
            }, [countdown, isTiming]);

            useEffect(() => {
                if (rootStore.isDev()) {
                    window.addEventListener('keydown', handleKeyDown);
                }
                window.addEventListener('click', resetInactivityCountdown);
                return () => {
                    window.removeEventListener('keydown', handleKeyDown);
                    window.removeEventListener('click', resetInactivityCountdown);
                };
            }, [handleKeyDown, rootStore]);

            return <></>;
        },
    ),
);
